/* modelResolver.js — THE one place that decides how a module instance renders
 * (hybrid-modular mandate §13: the 3D scene, the Module Editor stage and
 * Present must never each decide independently how to load models).
 *
 * CANONICAL SCHEMA (additive, technician-owned, rides the catalog unit and the
 * bridged def — u.model3d ≡ def.model3d, JS ≡ PY passthrough):
 *
 *   model3d = {
 *     mode: "full" | "component" | "hybrid" | "procedural",
 *     components: {
 *       carcass|front|drawer|handle|plinth|worktop: {
 *         source: "glb" | "procedural" | "material",
 *         model:  "/uploads/….glb"     (only when source === "glb")
 *       }
 *     }
 *   }
 *
 * LEGACY (no model3d): a technician-attached def.model → FULL, else
 * PROCEDURAL — byte-identical to the pre-mandate behavior, including the
 * "customised instance renders procedurally" rule.
 *
 * MODE SEMANTICS (deterministic — §6):
 *  · procedural  — engine geometry always (GLBs ignored even if present).
 *  · full        — the exact base GLB when the instance is pristine; ANY
 *                  visual override or CAD cut → procedural (edits must be
 *                  VISIBLE; a static GLB can't show them — §5/§12).
 *  · hybrid      — base GLB stays through visual overrides: hexes are painted
 *                  onto role-CLASSIFIED nodes (classifyGlbNode). If a wanted
 *                  role matches NO node the loader rejects the swap loudly and
 *                  the procedural build stands (§11 — never guess). CAD cuts
 *                  still force procedural (a baked GLB cannot be clipped, and
 *                  silently showing an uncut cabinet is forbidden — §12).
 *  · component   — procedural skeleton from the canonical geometry chain
 *                  (buildCabinetSpecs) + per-component GLBs fitted ONTO the
 *                  engine part boxes (door leaves under their hinge pivots, so
 *                  animation keeps working). A missing/failed component simply
 *                  keeps its procedural part. Cut instances render fully
 *                  procedurally (cuts must be visible — §12).
 *
 * Pure module — no THREE here. Scene-side helpers live in
 * scene3d/glbAssembly.js and consume these classifications.
 */

import { cutFrontConflict } from "./cabinetCuts";
import { mayCarryWorktop } from "./families";
import { activeFrontLayout, frontRowsOf, layoutRowsSummary } from "./frontLayout";

export const MODEL_MODES = ["full", "component", "hybrid", "procedural"];
export const COMPONENT_ROLES = ["carcass", "front", "drawer", "handle", "plinth", "worktop"];

/** normalizeModel3d(def) → { mode, components } with strict field hygiene.
 *  Unknown modes / malformed components are dropped (defensive: the blob is
 *  technician-authored JSON). Legacy defs resolve to full/procedural. */
export function normalizeModel3d(def) {
  const m3 = def && def.model3d;
  if (m3 && typeof m3 === "object" && MODEL_MODES.indexOf(m3.mode) >= 0) {
    const components = {};
    COMPONENT_ROLES.forEach((role) => {
      const c = m3.components && m3.components[role];
      if (!c || typeof c !== "object") return;
      const source = (c.source === "glb" || c.source === "procedural" || c.source === "material") ? c.source : null;
      if (!source) return;
      const model = source === "glb" && typeof c.model === "string" && c.model ? c.model : null;
      if (source === "glb" && !model) return;      // a GLB component without a file is undefined
      components[role] = { source, model };
      // MATERIAL SLOT (Stage 1, 2026-08-04): a material-slot component carries
      // the technician's DEFAULT material id (+ optional grain rotation — a
      // SLOT property, wood fronts rotate per part, the material doesn't).
      // A material slot with no materialId stays inert — exactly the dormant
      // pre-Stage-1 behavior, so previously-saved slots change nothing.
      if (source === "material") {
        const mid = typeof c.materialId === "string" && c.materialId ? c.materialId : null;
        if (mid) components[role].materialId = mid;
        if (c.grainRotDeg === 90) components[role].grainRotDeg = 90;
        // per-slot ALLOW-LIST (Stage 3b): which library materials the customer
        // may pick for this part. Empty/absent = every library material.
        if (Array.isArray(c.allow)) {
          const a = c.allow.filter((x) => typeof x === "string" && x);
          if (a.length) components[role].allow = a;
        }
      }
    });
    return { mode: m3.mode, components };
  }
  return { mode: def && def.model ? "full" : "procedural", components: {} };
}

/** materialSlotsOf(m3normalized) → { role: { materialId, grainRotDeg } } for
 *  every component the technician flipped to a Material slot WITH a default
 *  material assigned. The render paths consume this via vis.materialByRole. */
export function materialSlotsOf(m3) {
  const out = {};
  Object.keys((m3 && m3.components) || {}).forEach((role) => {
    const c = m3.components[role];
    if (c && c.source === "material" && c.materialId) {
      out[role] = { materialId: c.materialId, grainRotDeg: c.grainRotDeg === 90 ? 90 : 0 };
      if (c.allow) out[role].allow = c.allow;
    }
  });
  return out;
}

function collectComponentGlbs(m3) {
  const out = {};
  COMPONENT_ROLES.forEach((role) => {
    const c = m3.components[role];
    if (c && c.source === "glb" && c.model) out[role] = c.model;
  });
  return out;
}

/** resolveModuleVisual(def, config, vis, cutsCount) → the ONE render decision.
 *  vis = resolveConfigVisual(blob, type, config) output (hex overrides).
 *  Returns {
 *    mode,                  resolved model mode (after legacy mapping)
 *    baseGlb,               url of the base model to load (null = none)
 *    applyToGlb,            vis overrides to paint onto classified base nodes
 *                           (hybrid only; null = load the GLB as-is)
 *    componentGlbs,         { role: url } to fit onto engine part boxes
 *                           (component mode only)
 *    reason                 human-readable decision trail (logged by loaders)
 *  } */
export function resolveModuleVisual(def, config, vis, cutsCount, notchSides) {
  const m3 = normalizeModel3d(def);
  // MATERIAL SLOTS (Stage 1): the def's material-slot defaults ride the vis
  // object additively — role→{hex} became role→{hex, materialByRole?}. The
  // hex chain stays untouched (guaranteed fallback), so a def without slots
  // renders byte-identically.
  const matSlots = materialSlotsOf(m3);
  // CUSTOMER MATERIAL PICKS (Stage 3b): config.materials = { role: materialId }
  // overrides the slot's DEFAULT. Only roles the technician declared as
  // material slots are configurable — a pick on a non-slot role is ignored
  // (never guessed). The grain stays slot-owned.
  const cfgMats = (config && config.materials) || null;
  if (cfgMats && typeof cfgMats === "object") {
    Object.keys(cfgMats).forEach((role) => {
      const pick = cfgMats[role];
      if (matSlots[role] && typeof pick === "string" && pick) matSlots[role] = { ...matSlots[role], materialId: pick };
    });
  }
  const hasMats = Object.keys(matSlots).length > 0;
  const visM = hasMats ? { ...(vis || {}), materialByRole: matSlots } : vis;
  const overridden = anyVisualOverride(vis) || hasMats;
  const cut = (cutsCount || 0) > 0;
  const mapping = (def && def.model3dMeta && def.model3dMeta.map) || null;
  const avail0 = rolesAvailable(def && def.model3dMeta);
  // glbPlan — the PER-ROLE assembly plan (canonical-assembly mandate):
  //   swap:true   → the GLB replaces the procedural build (full/hybrid), with
  //                 replaceRoles hidden in the GLB + component GLBs attached at
  //                 the engine anchors, and mapped front/drawer/handle buckets
  //                 re-parented into the engine hinge pivots / slide groups so
  //                 REAL GLB doors & drawer boxes animate.
  //   swap:false  → partial overlay (cut case): the procedural CLIPPED deep
  //                 parts stay; only the kept GLB buckets (fronts/drawers/
  //                 handles the cut does not touch) render on top.
  const _animatable = ["front", "drawer", "handle"].filter((r) => avail0.indexOf(r) >= 0);
  // WORKTOP OWNERSHIP (2026-08-09, user mandate "I didn't upload a worktop —
  // don't add one"): a GLB module renders EXACTLY what was uploaded. The
  // engine's counter/run slabs appear ONLY when a worktop is explicitly
  // CHOSEN for the module (config.countertop — the priced OPT-WORKTOP pick),
  // and never when the model itself already contains a worktop part.
  // ownsWorktop=true ⇒ the engine adds nothing on top.
  const _cfgTop = !!(config && typeof config.countertop === "string" && config.countertop && config.countertop !== "None");
  // …and never on a module that cannot carry one (a tall / wall unit, a furniture
  // line — engine/families, 2026-09-29): the pick is inert there, nothing is added.
  const _ownsTop = avail0.indexOf("worktop") >= 0 || !_cfgTop || !mayCarryWorktop(def);
  // GOLA / PLINTH OWNERSHIP (2026-08-15 — user correction: "it just adds a
  // gola on TOP of the gola that module has itself; the GLB files have their
  // OWN golas"). An uploaded model is the physical truth: the engine NEVER
  // bolts a gola or a kickboard onto it. A gola pick recolours the model's own
  // gola part when one is identifiable (classifier or technician mapping) and
  // otherwise changes nothing but says so. Standalone gola profiles between
  // modules are a separate, explicitly placed element (Room → Gola 3D).
  // A GLB always owns both roles ⇒ nothing is ever added on top.
  const _ownsGola = true;
  const _ownsPlinth = true;
  const out = { mode: m3.mode, baseGlb: null, applyToGlb: null, componentGlbs: {}, mapping, glbPlan: null, reason: "", materialByRole: hasMats ? matSlots : null };

  /* HANDLE MODEL FROM THE HANDLE LIBRARY (2026-08-09): the technician can now
   * attach a GLB to a HANDLE (Handles → Attach 3D). configVisual surfaces the
   * chosen handle's model as vis.handleModel; mounting it here — on the ONE
   * resolver — gives every surface (orbit, Present, bench) the real handle
   * geometry at the engine's handle anchors, for procedural AND GLB modules.
   * A per-unit component/replacement declared by the technician still wins. */
  const _handleModel = (vis && vis.handleModel) || null;
  // pre-set so EVERY branch carries it (the procedural branch returns early);
  // a technician-declared component for the role still overrides it below.
  if (_handleModel) out.componentGlbs.handle = _handleModel;

  if (m3.mode === "procedural") {
    out.reason = "technician: procedural";
    return out;
  }

  // FRONT LAYOUT VARIANTS (Phase 2): a selected layout replaces the front
  // GEOMETRY with technician-defined rows — a genuine physical change a baked
  // GLB front cannot represent. Geometry-canon compatible policy:
  //  · GLB with identifiable fronts (mapped or auto-classified) → the GLB BODY
  //    is kept, its front/drawer/handle buckets hide, and the procedural ROW
  //    fronts render at the engine anchors (they animate normally);
  //  · unidentifiable fronts → fully procedural, stated loudly (hiding an
  //    unknown front bucket is impossible without doubling geometry);
  //  · layout + cut together → fully procedural (both are procedural edits).
  const _layout = activeFrontLayout(def, config);
  if (_layout) {
    const _rows = frontRowsOf(def, config);
    const _lt = "front layout '" + (_layout.label || _layout.id) + "' [" + layoutRowsSummary(_rows) + "]";
    if (def && def.model && (m3.mode === "hybrid" || m3.mode === "full") && !cut && avail0.indexOf("front") >= 0) {
      out.baseGlb = def.model;
      if (overridden) out.applyToGlb = visM;
      out.glbPlan = { swap: true, keepRoles: null, replaceRoles: {}, animate: false, procFronts: true, ownsWorktop: _ownsTop, ownsGola: _ownsGola, ownsPlinth: _ownsPlinth };
      out.reason = _lt + " → GLB body kept + procedural row fronts";
      return out;
    }
    if (m3.mode === "component" && !cut) {
      out.componentGlbs = { ...(_handleModel ? { handle: _handleModel } : {}), ...collectComponentGlbs(m3) };
      out.reason = _lt + " → component assembly on the row anchors";
      return out;
    }
    out.reason = _lt + " → procedural" + (def && def.model
      ? (cut ? " (cut instance — cuts must be visible)"
        : " (GLB fronts not identifiable — map the front parts in the technician to keep the carcass)")
      : "");
    return out;
  }

  /* GOLA NEVER CHANGES THE RENDER MODE (2026-08-24f user: "plinth is removed,
   * door is longer — the modules change totally, they are not how they need
   * to be"). The earlier gola/notch procFronts branch replaced the upload's
   * authored door with the engine leaf, which broke the model's own
   * proportions (door length, plinth). A golaPos or a neighbour's gola now
   * leaves the render decision COMPLETELY untouched — the upload renders
   * exactly as authored, and the standard 18×18 pocket is boolean-subtracted
   * from the carcass meshes at attach time (spec.golaNotchPrisms; the
   * notchSides parameter is intentionally unused here). */

  // PER-ROLE CUT HANDLING (Phase 13): a cut instance with a mapped model keeps
  // the GLB fronts/drawers/handles the cut does not touch, over the procedural
  // CLIPPED deep parts — the whole cabinet is no longer forced procedural.
  // Requires: hybrid/full base model + mapped animatable roles + the cut NOT
  // crossing the front face (then fronts must clip procedurally with holes).
  if (cut && def && def.model && (m3.mode === "hybrid" || m3.mode === "full") && _animatable.length) {
    let frontSafe = false;
    try { frontSafe = !cutFrontConflict(config, def); } catch (e) { frontSafe = false; }
    if (frontSafe) {
      out.baseGlb = def.model;
      if (overridden) out.applyToGlb = visM;
      out.glbPlan = { swap: false, keepRoles: _animatable, replaceRoles: {}, animate: true, ownsWorktop: _ownsTop, ownsGola: _ownsGola, ownsPlinth: _ownsPlinth };
      out.reason = "cut → clipped procedural deep parts + kept GLB " + _animatable.join("/");
      return out;
    }
  }

  if (m3.mode === "component") {
    if (!cut) {
      out.componentGlbs = { ...(_handleModel ? { handle: _handleModel } : {}), ...collectComponentGlbs(m3) };
      out.reason = "component assembly (" + Object.keys(out.componentGlbs).join(",") + ")";
    } else {
      out.reason = "cut instance → fully procedural (cuts must be visible)";
    }
    return out;
  }

  /* ── GEOMETRY CANON RULE (final-bug policy inversion, 2026-07-28) ─────────
   * The uploaded GLB is the canonical physical truth. A VISUAL change (colour /
   * material pick) must NEVER replace it with a procedural cabinet:
   *   · mapped roles     → recoloured on the real assembly;
   *   · unmapped roles   → keep their ORIGINAL look and are REPORTED
   *                        (unmappedRoles + reason — surfaced by the debug
   *                        log; the technician fixes it by mapping the part).
   * Only a GEOMETRY change the GLB genuinely cannot represent (a CAD cut on an
   * unmapped model) may leave the GLB — and that reason is stated. */
  if (def && def.model && !cut) {
    out.baseGlb = def.model;
    if (overridden) out.applyToGlb = visM;           // lenient paint: matched roles recolour, rest keep original
    const wanted = overridden ? rolesWantedByOverrides(visM) : [];
    const unmapped = wanted.filter((r) => avail0.indexOf(r) < 0);
    out.unmappedRoles = unmapped;
    // TRUE HYBRID (Phase 4): declared component GLBs replace ONLY their role —
    // the GLB bucket of that role is hidden and the component attaches at the
    // engine anchors (doors inside hinge pivots). Only roles the mapping can
    // actually identify are replaceable (we must be able to HIDE the original;
    // an unidentifiable role would double the geometry — never allowed).
    const replaceRoles = {};
    if (m3.mode === "hybrid") {
      Object.keys(m3.components).forEach((role) => {
        const c = m3.components[role];
        if (c && c.source === "glb" && c.model && avail0.indexOf(role) >= 0) replaceRoles[role] = c.model;
      });
    }
    // THE CHOSEN HANDLE ALWAYS WINS (2026-08-09 user mandate: "it must be 100%
    // the handle I uploaded"). A handle is an add-on the customer picked, so it
    // mounts on the engine's handle anchor even when the cabinet GLB has no
    // identifiable handle part — that also keeps the hinge pivots alive
    // (wantsFrontGroups), which is what carries the component. When the GLB DOES
    // expose a handle bucket it is hidden first, so nothing ever doubles.
    if (_handleModel && !replaceRoles.handle) replaceRoles.handle = _handleModel;
    // …and MOUNT IT EXACTLY ONCE: the parts loop also consumes
    // componentGlbs.handle, which mounted a second copy at the same anchor
    // (two overlapping handles, one of them hidden or not depending on load
    // order — the "that's not my handle" report).
    if (replaceRoles.handle) delete out.componentGlbs.handle;
    // LEGACY ATTACHMENTS (2026-07-31 fix): a GLB attached before upload-time
    // metadata existed (or bundled under /catalog-models) has NO meta at all —
    // avail0 is empty because nothing was ever ANALYSED, not because the model
    // has no doors. Predicting "not animatable" there deleted the engine hinge
    // pivots, so double-click could never open those cabinets. When metadata is
    // SILENT the loaders discover the roles from the real loaded scene graph
    // (same classifier) and animate what they actually find. Metadata that DOES
    // exist still decides deterministically — no behaviour change there.
    const discoverRoles = avail0.length === 0;
    out.glbPlan = { swap: true, keepRoles: null, replaceRoles, animate: _animatable.length > 0, discoverRoles, ownsWorktop: _ownsTop, ownsGola: _ownsGola, ownsPlinth: _ownsPlinth };
    out.reason = (overridden
      ? "real glb kept — recolour " + (wanted.filter((r) => unmapped.indexOf(r) < 0).join("/") || "(none mappable)")
        + (unmapped.length ? "; UNMAPPED kept original: " + unmapped.join("/") + " (map the part in the technician to recolour it)" : "")
      : "full glb")
      + (Object.keys(replaceRoles).length ? " + replace " + Object.keys(replaceRoles).join("/") : "")
      + (_animatable.length ? " (animatable: " + _animatable.join("/") + ")"
        : discoverRoles ? " (no metadata — animatable parts discovered at load)" : "");
    return out;
  }

  // PROCEDURAL module: the chosen handle's model still mounts on the engine's
  // handle anchors (componentGlbs.handle is fitted there by both renderers).
  if (_handleModel && !out.componentGlbs.handle) out.componentGlbs.handle = _handleModel;
  out.reason = (!def || !def.model ? "no glb → procedural"
    : "cut → procedural (front-crossing cut, or unmapped model: geometry must follow the cut — map the parts to keep the GLB fronts)")
    + (_handleModel ? " + handle model from the handle library" : "");
  return out;
}

/* ── GLB node classification (§11: conservative, NEVER guesses) ────────────
 * Matches explicit component naming in the model (multi-language: EN/IT).
 * Generic CAD export names ("3DGeom-1", "Assembly") match NOTHING and keep
 * their baked material. Order matters: specific roles before carcass. */
const NODE_PATTERNS = [
  // GOLA BEFORE handle/front/drawer (2026-08-15 user mandate "the system must
  // know the gola of the uploaded GLB directly"): a gola is the recessed
  // grip PROFILE — real exports name it "gola", "J-pull", "C-profile",
  // "profilo gola", "gripledge"/"griffleiste", "handleless rail". Those names
  // contain "handle"/"front" tokens too, so this rule must win. Conservative
  // (§11): only names that actually say gola/J-pull/grip-profile match.
  ["gola", /gola|j[\s_-]?pull|c[\s_-]?profile|grip[\s_-]?(ledge|rail|profile)|griff(leiste|profil)|profilo|handleless/i],
  // FABRIC (batch 14, 2026-10-03): the UPHOLSTERY of a sofa / armchair / lounger / bed — the
  // meshes a Fabric Library pick (and its uploaded photo) paints. Before front/carcass: a
  // "Front cushion" or "Back cushion" is upholstery, never a door or a carcass back.
  ["fabric", /fabric|upholster|cushion|pillow|textile|cloth|leather|pelle|tessut|stoff|velvet|boucl|(?:^|[^a-z])seat|backrest|armrest|mattress|(?:^|[^a-z])rope(?:[^a-z]|$)/i],
  // drawer BEFORE front: "drawer-front"/"drawerfront" are drawer parts (§10)
  ["drawer", /drawer|cassett/i],
  ["front", /door|front|leaf|anta/i],
  ["handle", /handle|maniglia|grip|knob/i],
  ["plinth", /plinth|kick|socle|zoccol/i],
  // "top" counts as a standalone token only ("Top_1"/"top-panel", never "stop")
  ["worktop", /worktop|counter|(?:^|[^a-z])top(?:[^a-z]|$)|piano/i],
  ["carcass", /carcas[se]|body|corpus|cabinet|fianco|\bside\b|shelf|\bback\b/i],
];
export function classifyGlbNode(name) {
  const n = String(name || "");
  if (!n) return null;
  for (const [role, re] of NODE_PATTERNS) {
    if (re.test(n)) return role;
  }
  return null;
}

/* ── TECHNICIAN ROLE MAPPING (forensic-audit fix, 2026-07-28) ──────────────
 * Real manufacturer GLBs carry their meaning in MATERIAL names ("[Color M06]")
 * or a handful of node names ("Front1", "Shelf") that automatic classification
 * cannot safely interpret. The technician assigns them explicitly:
 *   model3dMeta.map = { role: { mats: ["[Color M06]"], nodes: ["Front1"] } }
 * The mapping is CANONICAL metadata — the same painter consumes it in the
 * orbit scene, the Module Editor stage and Present, so an option pick
 * recolours the REAL uploaded assembly instead of dropping to a procedural
 * substitute (the root cause of "Technician shows a different cabinet"). */

/** classifyByMap(nodeName, matName, map) → role | null (exact-name matches). */
export function classifyByMap(nodeName, matName, map) {
  if (!map) return null;
  const nn = String(nodeName || "");
  const mn = String(matName || "");
  for (const role of Object.keys(map)) {
    const m = map[role] || {};
    if (mn && Array.isArray(m.mats) && m.mats.indexOf(mn) >= 0) return role;
    if (nn && Array.isArray(m.nodes) && m.nodes.indexOf(nn) >= 0) return role;
  }
  return null;
}

/** rolesAvailable(meta) → auto-classified roles ∪ technician-mapped roles —
 *  what the hybrid decision can count on finding in the base GLB. */
export function rolesAvailable(meta) {
  const out = new Set();
  if (meta && Array.isArray(meta.roles)) meta.roles.forEach((r) => out.add(r));
  if (meta && meta.map) Object.keys(meta.map).forEach((r) => {
    const m = meta.map[r] || {};
    if ((Array.isArray(m.mats) && m.mats.length) || (Array.isArray(m.nodes) && m.nodes.length)) out.add(r);
  });
  return Array.from(out);
}

/** Which vis-hex key recolours which classified role (drawer fronts take the
 *  front colour — same rule as the procedural frontOf material). */
export const OVERRIDE_HEX_BY_ROLE = {
  carcass: "carcHex",
  front: "frontHex",
  drawer: "frontHex",
  handle: "handleHex",
  gola: "golaHex",                                   // 2026-08-15: own finish
  plinth: "plinthHex",
  worktop: "worktopHex",
  // interior is PROTECTED from carcass picks; it recolours ONLY through the
  // explicit Interior Finish option (vis.interiorHex — Phase 1).
  interior: "interiorHex",
};

/** rolesWantedByOverrides(vis) → the roles a hybrid recolour must find in the
 *  base GLB for the swap to be accepted (front falls back to carcHex exactly
 *  like the procedural frontOf chain, so carcHex alone wants front too). */
export function rolesWantedByOverrides(vis) {
  if (!vis) return [];
  const wanted = new Set();
  if (vis.carcHex) { wanted.add("carcass"); wanted.add("front"); }
  if (vis.frontHex) wanted.add("front");
  if (vis.frontGlass) wanted.add("front");           // glass front style (Phase 3)
  if (vis.handleHex) wanted.add("handle");
  if (vis.golaHex) wanted.add("gola");
  if (vis.plinthHex) wanted.add("plinth");
  if (vis.worktopHex) wanted.add("worktop");
  if (vis.interiorHex) wanted.add("interior");
  // TECHNICIAN SWATCH PHOTOS (2026-08-15): a colour record's uploaded texture
  // wants its role painted exactly like a hex does — otherwise a texture-only
  // finish silently never reached the GLB painter.
  if (vis.carcTex) { wanted.add("carcass"); wanted.add("front"); }
  if (vis.frontTex) wanted.add("front");
  if (vis.golaTex) wanted.add("gola");
  if (vis.plinthTex) wanted.add("plinth");
  // 2026-09-01 exact-image: photo-only worktop/backsplash finishes were the
  // one pair still missing here - a GLB-owned worktop never painted them
  if (vis.worktopTex) wanted.add("worktop");
  if (vis.backsplashTex) wanted.add("backsplash");
  if (vis.fabricHex || vis.fabricTex) wanted.add("fabric");   // batch 14: the upholstery
  // MATERIAL SLOTS (Stage 1): a slot with a default material wants its role
  // painted exactly like a hex override does (same acceptance/report rules).
  Object.keys(vis.materialByRole || {}).forEach((r) => wanted.add(r));
  return Array.from(wanted);
}

/** anyVisualOverride(vis) — ONE predicate for "this config changes how the
 *  module looks" (hex, swatch photo, material slot or glass style). Used by
 *  the resolver so a texture-only or gola-only pick still paints the GLB. */
export function anyVisualOverride(vis) {
  return !!(vis && (vis.carcHex || vis.frontHex || vis.handleHex || vis.golaHex || vis.plinthHex
    || vis.worktopHex || vis.interiorHex || vis.frontGlass
    || vis.carcTex || vis.frontTex || vis.golaTex || vis.plinthTex
    || vis.fabricHex || vis.fabricTex));
}
